import { create } from 'zustand';
import { createStorage, STORAGE_KEYS } from '@/shared/storage';
import type {
  AgentId,
  AgentWorkspaceState,
  ChatLoginScope,
  ChatWorkspaceSnapshot,
} from './types';
import {
  createChatWorkspaceChannelName,
  createChatWorkspaceStorageKey,
} from './keys';

const storage = createStorage('local');
const WORKSPACE_STORAGE_BASE = `${STORAGE_KEYS.chatWorkspace}:v2:`;

type ChatWorkspaceState = ChatWorkspaceSnapshot & {
  isHydrated: boolean;
  creatingNewChatAgentIds: string[];
};

type ChatWorkspaceActions = {
  configureSession: (scope: ChatLoginScope) => void;
  clearSession: () => void;
  setCreatingNewChat: (agentId: AgentId, pending: boolean) => void;
  markThreadActive: (agentId: AgentId, threadId: string, hasUserStartedChat?: boolean, recoveryRunId?: string | null) => void;
  clearRecoveryRun: (agentId: AgentId, threadId: string, expectedRunId?: string | null) => void;
  clearActiveThread: (agentId: AgentId, expectedThreadId?: string | null) => void;
  pruneAgents: (agentIds: readonly AgentId[]) => void;
  resetWorkspace: () => void;
};

type WorkspaceStore = ChatWorkspaceState & ChatWorkspaceActions;

type RuntimeState = {
  scope: ChatLoginScope | null;
  key: string | null;
  channel: BroadcastChannel | null;
  storageListenerInstalled: boolean;
};

const runtime: RuntimeState = {
  scope: null,
  key: null,
  channel: null,
  storageListenerInstalled: false,
};

function nowIso(): string {
  return new Date().toISOString();
}

function emptySnapshot(scope: ChatLoginScope): ChatWorkspaceSnapshot {
  return {
    version: 2,
    userId: scope.userId,
    loginSessionId: scope.loginSessionId,
    agents: {},
    resetGeneration: 0,
    lastResetAt: null,
  };
}

function workspaceStorageKey(scope: ChatLoginScope): string {
  return createChatWorkspaceStorageKey(scope);
}

function workspaceChannel(scope: ChatLoginScope): string {
  return createChatWorkspaceChannelName(scope);
}

function isWorkspaceSnapshot(value: unknown): value is ChatWorkspaceSnapshot {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return record.version === 2
    && typeof record.userId === 'string'
    && typeof record.loginSessionId === 'string'
    && typeof record.resetGeneration === 'number'
    && typeof record.agents === 'object'
    && record.agents !== null;
}

function compareIso(a: string | null | undefined, b: string | null | undefined): number {
  const left = a ?? '';
  const right = b ?? '';
  if (left === right) return 0;
  return left > right ? 1 : -1;
}

function normalizeAgentState(
  resetAt: string | null,
  agent: AgentWorkspaceState | undefined,
): AgentWorkspaceState | null {
  if (!agent) return null;
  if (resetAt && compareIso(agent.lastTouchedAt, resetAt) <= 0) return null;
  return agent;
}

function mergeSnapshots(
  base: ChatWorkspaceSnapshot,
  incoming: ChatWorkspaceSnapshot,
): ChatWorkspaceSnapshot {
  if (base.userId !== incoming.userId || base.loginSessionId !== incoming.loginSessionId) {
    return base;
  }

  const mergedResetAt = compareIso(base.lastResetAt, incoming.lastResetAt) >= 0
    ? base.lastResetAt
    : incoming.lastResetAt;

  const agents: Record<AgentId, AgentWorkspaceState> = {};
  const ids = new Set([...Object.keys(base.agents), ...Object.keys(incoming.agents)]);
  for (const agentId of ids) {
    const left = normalizeAgentState(mergedResetAt, base.agents[agentId]);
    const right = normalizeAgentState(mergedResetAt, incoming.agents[agentId]);
    if (!left && !right) continue;
    if (!left) {
      agents[agentId] = right!;
      continue;
    }
    if (!right) {
      agents[agentId] = left;
      continue;
    }
    agents[agentId] = compareIso(left.lastTouchedAt, right.lastTouchedAt) >= 0 ? left : right;
  }

  return {
    version: 2,
    userId: base.userId,
    loginSessionId: base.loginSessionId,
    agents,
    resetGeneration: Math.max(base.resetGeneration, incoming.resetGeneration),
    lastResetAt: mergedResetAt,
  };
}

function readSnapshot(scope: ChatLoginScope): ChatWorkspaceSnapshot {
  const raw = storage.get<unknown>(workspaceStorageKey(scope), null);
  return isWorkspaceSnapshot(raw) ? raw : emptySnapshot(scope);
}

function cleanupWorkspaceKeys(scope: ChatLoginScope): void {
  if (typeof window === 'undefined') return;
  try {
    const { localStorage } = window;
    const userPrefix = `${WORKSPACE_STORAGE_BASE}${encodeURIComponent(scope.userId)}:`;
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key || !key.startsWith(userPrefix) || key === workspaceStorageKey(scope)) continue;
      localStorage.removeItem(key);
    }
  } catch {
    // ignore storage access errors
  }
}

function persistSnapshot(snapshot: ChatWorkspaceSnapshot): void {
  storage.set(workspaceStorageKey(snapshot), snapshot);
}

function attachBroadcast(scope: ChatLoginScope): void {
  runtime.channel?.close();
  runtime.channel = null;

  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(workspaceChannel(scope));
    channel.onmessage = (event: MessageEvent<unknown>) => {
      const payload = event.data;
      if (!isWorkspaceSnapshot(payload)) return;
      useChatWorkspaceStore.setState((state) => {
        const current = state.userId && state.loginSessionId
          ? {
              version: 2 as const,
              userId: state.userId,
              loginSessionId: state.loginSessionId,
              agents: state.agents,
              resetGeneration: state.resetGeneration,
              lastResetAt: state.lastResetAt,
            }
          : emptySnapshot(scope);
        const merged = mergeSnapshots(current, payload);
        return {
          ...state,
          ...merged,
          isHydrated: true,
        };
      });
    };
    runtime.channel = channel;
  }

  if (!runtime.storageListenerInstalled && typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (!runtime.key || event.key !== runtime.key || !runtime.scope) return;
      const scope = runtime.scope;
      if (event.newValue === null) {
        useChatWorkspaceStore.setState((state) => ({
          ...state,
          ...emptySnapshot(scope),
          isHydrated: true,
          creatingNewChatAgentIds: [],
        }));
        return;
      }
      const next = readSnapshot(scope);
      useChatWorkspaceStore.setState((state) => {
        const current = state.userId && state.loginSessionId
          ? {
              version: 2 as const,
              userId: state.userId,
              loginSessionId: state.loginSessionId,
              agents: state.agents,
              resetGeneration: state.resetGeneration,
              lastResetAt: state.lastResetAt,
            }
          : emptySnapshot(scope);
        const merged = mergeSnapshots(current, next);
        return {
          ...state,
          ...merged,
          isHydrated: true,
        };
      });
    });
    runtime.storageListenerInstalled = true;
  }
}

function writeSnapshot(snapshot: ChatWorkspaceSnapshot): void {
  persistSnapshot(snapshot);
  runtime.channel?.postMessage(snapshot);
}

function currentSnapshot(state: WorkspaceStore): ChatWorkspaceSnapshot | null {
  if (!state.userId || !state.loginSessionId) return null;
  return {
    version: 2,
    userId: state.userId,
    loginSessionId: state.loginSessionId,
    agents: state.agents,
    resetGeneration: state.resetGeneration,
    lastResetAt: state.lastResetAt,
  };
}

function withMergedSnapshot(
  state: WorkspaceStore,
  recipe: (base: ChatWorkspaceSnapshot) => ChatWorkspaceSnapshot,
): ChatWorkspaceSnapshot | null {
  const scope = runtime.scope;
  if (!scope) return null;
  const current = currentSnapshot(state) ?? emptySnapshot(scope);
  const persisted = readSnapshot(scope);
  const base = mergeSnapshots(current, persisted);
  return recipe(base);
}

export const useChatWorkspaceStore = create<WorkspaceStore>((set) => ({
  ...emptySnapshot({ userId: '', loginSessionId: '' }),
  userId: '',
  loginSessionId: '',
  isHydrated: false,
  creatingNewChatAgentIds: [],

  configureSession: (scope) => {
    runtime.scope = scope;
    runtime.key = workspaceStorageKey(scope);
    cleanupWorkspaceKeys(scope);
    attachBroadcast(scope);
    const snapshot = readSnapshot(scope);
    persistSnapshot(snapshot);
    set({
      ...snapshot,
      isHydrated: true,
      creatingNewChatAgentIds: [],
    });
  },

  clearSession: () => {
    if (runtime.key) storage.remove(runtime.key);
    runtime.channel?.close();
    runtime.channel = null;
    runtime.scope = null;
    runtime.key = null;
    set({
      ...emptySnapshot({ userId: '', loginSessionId: '' }),
      userId: '',
      loginSessionId: '',
      isHydrated: false,
      creatingNewChatAgentIds: [],
    });
  },

  setCreatingNewChat: (agentId, pending) => {
    set((state) => ({
      creatingNewChatAgentIds: pending
        ? Array.from(new Set([...state.creatingNewChatAgentIds, agentId]))
        : state.creatingNewChatAgentIds.filter((id) => id !== agentId),
    }));
  },

  markThreadActive: (agentId, threadId, hasUserStartedChat = true, recoveryRunId) => {
    set((state) => {
      const next = withMergedSnapshot(state, (base) => {
        const existing = base.agents[agentId];
        return {
          ...base,
          agents: {
            ...base.agents,
            [agentId]: {
              activeThreadId: threadId,
              recoveryRunId: recoveryRunId ?? (existing?.activeThreadId === threadId ? existing.recoveryRunId ?? null : null),
              hasUserStartedChat,
              lastTouchedAt: nowIso(),
            },
          },
        };
      });
      if (next) writeSnapshot(next);
      return next
        ? {
            ...state,
            ...next,
          }
        : state;
    });
  },

  clearRecoveryRun: (agentId, threadId, expectedRunId = null) => {
    set((state) => {
      const next = withMergedSnapshot(state, (base) => {
        const existing = base.agents[agentId];
        if (!existing || existing.activeThreadId !== threadId) return base;
        if (expectedRunId !== null && existing.recoveryRunId !== expectedRunId) return base;
        return {
          ...base,
          agents: {
            ...base.agents,
            [agentId]: { ...existing, recoveryRunId: null },
          },
        };
      });
      if (next) writeSnapshot(next);
      return next ? { ...state, ...next } : state;
    });
  },

  clearActiveThread: (agentId, expectedThreadId = null) => {
    set((state) => {
      const next = withMergedSnapshot(state, (base) => {
        const existing = base.agents[agentId];
        if (!existing) return base;
        if (expectedThreadId !== null && existing.activeThreadId !== expectedThreadId) {
          return base;
        }
        return {
          ...base,
          agents: {
            ...base.agents,
            [agentId]: {
              ...existing,
              activeThreadId: null,
              recoveryRunId: null,
              hasUserStartedChat: false,
              lastTouchedAt: nowIso(),
            },
          },
        };
      });
      if (next) writeSnapshot(next);
      return next
        ? {
            ...state,
            ...next,
          }
        : state;
    });
  },

  pruneAgents: (agentIds) => {
    const allowed = new Set(agentIds);
    set((state) => {
      const next = withMergedSnapshot(state, (base) => ({
        ...base,
        agents: Object.fromEntries(
          Object.entries(base.agents).filter(([agentId]) => allowed.has(agentId)),
        ),
      }));
      if (next) writeSnapshot(next);
      return next
        ? {
            ...state,
            ...next,
          }
        : state;
    });
  },

  resetWorkspace: () => {
    set((state) => {
      const next = withMergedSnapshot(state, (base) => ({
        ...base,
        agents: {},
        resetGeneration: base.resetGeneration + 1,
        lastResetAt: nowIso(),
      }));
      if (next) writeSnapshot(next);
      return next
        ? {
            ...state,
            ...next,
            creatingNewChatAgentIds: [],
          }
        : state;
    });
  },
}));
